const AUTHENTICATION_REPLACEMENT_GRACE_PERIOD_MS = 3000;

type SignOutCallback = () => void | Promise<void>;

export class MobileAuthenticationSignOutCoordinator {
  private pendingSignOutTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingReadiness: Promise<void> | null = null;
  private generation = 0;
  private nativeReauthenticationRequested = false;
  private nativeStateConfirmationRequested = false;
  private rejectedNativeAccounts = new Set<string>();
  private rejectedNativeSessions = new Set<string>();

  noteRejectedNativeSession(sessionIdentifier: string): void {
    this.rejectedNativeSessions.add(sessionIdentifier);
  }

  isRejectedNativeSession(sessionIdentifier: string | null): boolean {
    return sessionIdentifier !== null && this.rejectedNativeSessions.has(sessionIdentifier);
  }

  captureCurrentAuthentication(): () => boolean {
    const generation = this.generation;
    return () => generation === this.generation;
  }

  beginNativeReauthentication(userId: string, sessionIdentifier: string, requestLogout: () => void): void {
    this.noteRejectedNativeSession(sessionIdentifier);
    // Older clients may answer INIT_APP with the rejected account before their
    // native logout finishes. Refreshing that token keeps the same auth_time.
    // Bound the automatic logout per account for this page's lifetime; a fresh
    // successful sign-in does not need another containment logout.
    if (this.rejectedNativeAccounts.has(userId)) return;
    this.rejectedNativeAccounts.add(userId);
    this.nativeReauthenticationRequested = true;
    this.nativeStateConfirmationRequested = false;
    this.cancelPendingSignOut();
    requestLogout();
  }

  noteNativeAuthenticationReceived(): void {
    this.nativeStateConfirmationRequested = false;
    this.cancelPendingSignOut();
  }

  confirmNativeStateAfterSignOut(requestCurrentState: () => void): void {
    // LOGOUT replies are uncorrelated on released native clients and can
    // arrive after a replacement account. Ask native for its current state;
    // a genuine sign-out still clears auth and its empty reply ends the check.
    if (!this.nativeReauthenticationRequested || this.nativeStateConfirmationRequested) return;
    this.nativeStateConfirmationRequested = true;
    requestCurrentState();
  }

  requestSignOutWhenReady(ready: () => Promise<void>, signOut: SignOutCallback): Promise<void> {
    // Initial native-empty responses are provisional only until a native token
    // or authoritative sign-out arrives. Later account transitions use tokens
    // and real sign-outs; delayed initial responses cannot supersede either.
    if (this.generation !== 0) return Promise.resolve();
    if (this.pendingReadiness) return this.pendingReadiness;
    const generation = this.generation;
    const pending = Promise.resolve()
      .then(ready)
      .then(() => {
        // A replacement token or authoritative sign-out supersedes this
        // initial native-empty response while web persistence is restoring.
        if (generation !== this.generation) return;
        return signOut();
      })
      .finally(() => {
        if (this.pendingReadiness === pending) this.pendingReadiness = null;
      });
    this.pendingReadiness = pending;
    return pending;
  }

  requestSignOut(signOut: SignOutCallback): void {
    // Native Firebase restoration and token refresh can emit sign-out before a
    // replacement token on any app build. Keep the first deadline so duplicate
    // bridge events cannot defer a genuine logout indefinitely.
    if (this.pendingSignOutTimeout) return;

    this.pendingSignOutTimeout = setTimeout(() => {
      this.pendingSignOutTimeout = null;
      void signOut();
    }, AUTHENTICATION_REPLACEMENT_GRACE_PERIOD_MS);
  }

  cancelPendingSignOut(): void {
    this.generation += 1;
    this.pendingReadiness = null;
    if (!this.pendingSignOutTimeout) return;

    clearTimeout(this.pendingSignOutTimeout);
    this.pendingSignOutTimeout = null;
  }

  signOutImmediately(signOut: SignOutCallback): void | Promise<void> {
    this.cancelPendingSignOut();
    return signOut();
  }
}
